import { apiService } from "@/api";
import { getUserInfoGlobal } from "@/api/config";
import PanelFooter from "@/coral/components/Panels/PanelFooter";
import PanelLeft from "@/coral/components/Panels/PanelLeft";
import PanelLeftToolbar from "@/coral/components/Panels/PanelLeftToolbar";
import { PlanPanelLefProps, PlanWithSteps, Task, UserInfo } from "@/models";
import { TaskService } from "@/services";
import UserMenu from "@/components/UserMenu";
import {
  Body1Strong,
  Toast,
  ToastBody,
  ToastTitle,
  Tooltip,
  useToastController
} from "@fluentui/react-components";
import {
  ChatAdd20Regular,
  ErrorCircle20Regular
} from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EricssonLogo from "../../coral/imports/EricssonLogo";
import "../../styles/PlanPanelLeft.css";
import TaskList from "./TaskList";

const PlanPanelLeft: React.FC<PlanPanelLefProps> = ({ reloadTasks, restReload }) => {
  const { dispatchToast } = useToastController("toast");
  const navigate = useNavigate();
  const { planId } = useParams<{ planId: string }>();

  const [inProgressTasks, setInProgressTasks] = useState<Task[]>([]);
  const [completedTasks, setCompletedTasks] = useState<Task[]>([]);
  const [plans, setPlans] = useState<PlanWithSteps[] | null>(null);
  const [plansLoading, setPlansLoading] = useState<boolean>(false);
  const [plansError, setPlansError] = useState<Error | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(
    getUserInfoGlobal()
  );

  const loadPlansData = useCallback(async (forceRefresh = false) => {
    try {
      setPlansLoading(true);
      setPlansError(null);
      const plansData = await apiService.getPlans(undefined, !forceRefresh);
      setPlans(plansData);
    } catch (error) {
      console.log("Failed to load plans:", error);
      setPlansError(
        error instanceof Error ? error : new Error("Failed to load plans")
      );
    } finally {
      setPlansLoading(false);
    }
  }, []);

  useEffect(() => {
    if (reloadTasks) {
      loadPlansData();
      restReload?.();
    }
  }, [reloadTasks, loadPlansData, restReload]);
  // Fetch plans


  useEffect(() => {
    loadPlansData();
  }, [loadPlansData]);

  useEffect(() => {
    if (plans) {
      const { inProgress, completed } =
        TaskService.transformPlansToTasks(plans);
      setInProgressTasks(inProgress);
      setCompletedTasks(completed);
    }
  }, [plans]);

  useEffect(() => {
    if (plansError) {
      dispatchToast(
        <Toast>
          <ToastTitle>
            <ErrorCircle20Regular />
            Failed to load tasks
          </ToastTitle>
          <ToastBody>{plansError.message}</ToastBody>
        </Toast>,
        { intent: "error" }
      );
    }
  }, [plansError, dispatchToast]);

  // Get the session_id that matches the current URL's planId
  const selectedTaskId =
    plans?.find((plan) => plan.id === planId)?.session_id ?? null;

  const handleTaskSelect = useCallback(
    (taskId: string) => {
      const selectedPlan = plans?.find(
        (plan: PlanWithSteps) => plan.session_id === taskId
      );
      if (selectedPlan) {
        navigate(`/plan/${selectedPlan.id}`);
      }
    },
    [plans, navigate]
  );

  return (
    <div style={{ flexShrink: 0, display: "flex", overflow: "hidden" }}>
      <PanelLeft panelWidth={280} panelResize={true}>
        <PanelLeftToolbar
          linkTo="/"
          panelTitle="Ericsson v0.0.9 (panel)"
          panelIcon={<EricssonLogo />}
        >
          <Tooltip content="New task" relationship={"label"} />
        </PanelLeftToolbar>

        <div
          className="tab tab-new-task"
          onClick={() => navigate("/", { state: { focusInput: true } })}
          tabIndex={0} // ✅ allows tab focus
          role="button" // ✅ announces as button
          style={{ marginTop: '8px' }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate("/", { state: { focusInput: true } });
            }
          }}
        >
          <ChatAdd20Regular className="tab-new-task-icon" />
          <Body1Strong>New chat</Body1Strong>
        </div>

        <TaskList
          inProgressTasks={inProgressTasks}
          completedTasks={completedTasks}
          onTaskSelect={handleTaskSelect}
          loading={plansLoading}
          selectedTaskId={selectedTaskId ?? undefined}
        />

        <PanelFooter>
          <UserMenu userInfo={userInfo} />
        </PanelFooter>
      </PanelLeft>
    </div>
  );
};

export default PlanPanelLeft;
